// src/App.jsx

import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Menu, Lock, LogOut, Eye, ChevronDown } from 'lucide-react';

// FIREBASE
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore'; 
import { auth, db, functions } from './config/firebase-config';
import { httpsCallable } from 'firebase/functions';

// CONTEXT & UTILS
import { OrganizationProvider, useOrganization } from './context/OrganizationContext';
import { calculateScores } from './utils/scoring';
import { isSuperAdmin } from './config/permissions';
import { readPreviewPathProgress, PREVIEW_PROGRESS_EVENT } from './utils/guidedPath';
import { useJournalContext } from './hooks/useJournalContext';
import { copyFor, isCorporateOrg } from './data/copyDictionary';
import { isDashboardLocked } from './utils/pulse';
import { buildCoachContext } from './utils/coachContext';
import { resolveSurface } from './utils/resolveSurface';
import { resolveAgeTier } from './utils/ageTier';
import FinanceHubView from './components/finance/FinanceHubView';
import { FINANCE_TAB_ID, isFinanceHubEnabled } from './data/financeHub';
import { TEEN_EXCLUDED_TOPIC_IDS, NAV_TOPIC_GROUPS } from './data/navTopics';
import { isTabAllowed, allowedSubTopicIds, firstAllowedTopic } from './utils/navAccess';
import { findCustomSection, customTopicIds, isLandingSection } from './utils/customNav';
import CustomLandingView from './components/custom/CustomLandingView';
import { resolveMemberTypes, previewPersonasFor } from './data/memberTypes';

// CORE COMPONENTS
import Sidebar from './components/Sidebar';
import ConsoleSidebar from './components/enterprise/ConsoleSidebar';
import { isConsoleTab } from './components/enterprise/consoleTabs';
import AICoach, { MAIN_AGENT_CONFIG } from './components/AICoach';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import JoinOrgHandler from './components/JoinOrgHandler'; 
import LoadingScreen from './components/LoadingScreen';
import LoginModal from './components/LoginModal';
import UnauthLanding from './components/UnauthLanding';
import AssessmentView from './components/AssessmentView';

// THE 3-PILLAR PORTALS
import ExecutivePortal from './portals/ExecutivePortal';
import CoachPortal from './portals/CoachPortal';
import AthletePortal from './portals/AthletePortal';

// MEMBER-FUNNEL SURFACES (selected by resolveSurface)
import SpouseHubView from './components/SpouseHubView';
import TeenHubView from './components/TeenHubView';
import ParentHubView from './components/ParentHubView';
import KidzCorner from './components/KidzCorner';
import TopicView from './components/TopicView';
import { initialTopicContent } from './data/content';

// Member-surface content routing (Mental Health / Mental Performance dropdowns).
// Mental Performance is shared in full; Mental Health is age-curated — teens do
// NOT get Addictions/Gambling. Enforced here (not just in the sidebar) so a teen
// can't reach an excluded topic even via a direct hash link.
// Topic lists live in data/navTopics.js; what an ORG allows lives in its
// navConfig and is answered by utils/navAccess.js. Both layers apply here: the
// platform's teen age gate first, then the org's own allowlist on top.

// The topic id a member surface should render for the active tab, or null to
// show the hub. `allowedMH` / `allowedMP` gate which topics are reachable.
const resolveMemberTopic = (tab, allowedMH, allowedMP) => {
    if (tab === 'mental-health') return allowedMH[0] || null;
    if (tab === 'mental-performance') return allowedMP[0] || null;
    if (allowedMH.includes(tab) || allowedMP.includes(tab)) return tab;
    return null;
};

// =============================================================================
// PREVIEW SAMPLE DATA
// =============================================================================
// Representative `latestStats` attached ONLY to the synthetic preview user so
// super-admin preview mode renders a fully populated dashboard instead of the
// "Start Baseline" empty state. Values are deliberately healthy-but-realistic:
//   - Performance metrics (0-100, higher = better) average to perfScore 74,
//     with cognitive_score 80 -> eriScore 75 and a "Peak" readiness read.
//   - Clinical instrument sums (higher = worse) all sit in the none/mild band
//     (sleep_clinical 3/10 is the lone 'mild'), so wellbeingScore = 88 (tier
//     "Mild"), quadrant = Thriving, safetyFlag = false, no divergence flag.
const PREVIEW_SAMPLE_STATS = {
    // Performance axis (SCORING_CONFIG.performance.metrics)
    focus: 78,
    resilience: 82,
    motivation: 85,
    confidence: 74,
    discipline: 70,
    awareness: 68,
    adaptability: 72,
    sleep_perf: 76,
    nutrition: 64,
    injury_mgmt: 71,
    cognitive_score: 80,
    // Clinical screeners (raw sums, all none/mild — nothing near severe)
    anxiety: 3,          // GAD-7, 0-4 = none
    depression: 4,       // PHQ-9, 0-4 = none
    sleep_clinical: 3,   // 3/10 = 30% -> mild
    alcohol: 2,          // 2/12 -> none
    substance: 0,        // CAGE-AID -> none
    eating: 3,           // 3/18 -> none
    adhd: 4,             // 4/24 -> none
    trauma: 0,           // PC-PTSD-5 -> none
    gambling: 0          // PGSI -> none
};

// =============================================================================
// MAIN CONTENT WRAPPER (Inner App)
// =============================================================================
const AppContent = () => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // Contents of users/{uid}/private_metrics/latest (self-only scores/wearables).
  const [privateMetrics, setPrivateMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  // Releases the loading-screen holds (theme, half-joined account) a few seconds
  // after each sign-in, so nothing can keep the screen dark indefinitely.
  const [holdExpired, setHoldExpired] = useState(false);
  useEffect(() => {
      setHoldExpired(false);
      const t = setTimeout(() => setHoldExpired(true), 5000);
      return () => clearTimeout(t);
  }, [user]);
  const [assessmentDue, setAssessmentDue] = useState(false);
  
  // Individual scores + wearable summaries live in users/{uid}/private_metrics/latest
  // (self-only rules — admins/coaches can't read them). Merge them back into the
  // profile so downstream consumers keep reading profile.latestStats /
  // profile.wearables as before. The user-doc fields remain as a legacy
  // fallback until tools/migrate-private-metrics.js has run against prod.
  const hydratedProfile = React.useMemo(() => {
      if (!userProfile) return userProfile;
      const merged = { ...userProfile };
      if (privateMetrics?.latestStats) merged.latestStats = privateMetrics.latestStats;
      if (privateMetrics?.wearables) merged.wearables = privateMetrics.wearables;
      return merged;
  }, [userProfile, privateMetrics]);

  // Calculate Scores for AI Coach Insights
  // (scores are computed below from currentActiveUser — the previewed persona in
  // preview mode, the hydrated real profile otherwise — so the coach, the pulse
  // gate and the dashboard all describe the same person.)

  // Consume Organization Context
  const { orgData, isPreviewMode, enterPreviewMode, exitPreviewMode, previewPersona, switchPreviewPersona, previewRole, previewMemberType, previewAgeTier, themeLoaded } = useOrganization();

  // --- HANDLE INITIAL TAB & WEARABLE REDIRECT ---
  const getInitialTab = () => {
    if (new URLSearchParams(window.location.search).has('code')) {
        return 'dashboard'; // <-- Route wearable callbacks here now
    }
    // A join link as a PATH (/join/{org}/{team}[/staff/{code}]) is what the link
    // sheet hands out: a '#' in a link does not survive Excel, some mail clients
    // or an SMS preview. It becomes the hash route here, once, on load.
    const path = window.location.pathname || '';
    if (path.startsWith('/join/')) {
        const tab = path.slice(1).replace(/\/+$/, '');
        try { window.history.replaceState(null, '', `/#${tab}`); } catch (e) { /* the path still routes */ }
        return tab;
    }
    const hash = window.location.hash.replace('#', '');
    return hash || 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  // Force-route to a role's landing page only ONCE per session (initial profile
  // load), not on every user-doc onSnapshot update — otherwise any user-doc
  // write (e.g. guided-path progress, wearable sync) would yank admins back to
  // the executive console mid-session.
  const initialRouteDoneRef = React.useRef(false);
  const roleSyncRef = React.useRef(false); // server-side role sync attempted this session
  // Set when the server held a staff elevation because the email isn't verified.
  const [verifyPrompt, setVerifyPrompt] = useState(null); // { email, roleLabel }
  // Holds the active users/{uid} onSnapshot unsubscriber — the auth callback is
  // async, so a `return` inside it is ignored by Firebase; we must tear down
  // the previous profile listener ourselves on auth changes and unmount.
  const profileUnsubRef = React.useRef(null);
  // Same lifecycle for the users/{uid}/private_metrics/latest listener.
  const metricsUnsubRef = React.useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Collapse the preview HUD so it stays out of mockup screenshots.
  const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // AI Coach drawer — slides in from the right on every screen size (no longer a
  // pinned rail on xl+). Open state is remembered per browser.
  const [showAIChat, setShowAIChat] = useState(() => {
    try { return localStorage.getItem('eri_ai_chat_open') === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('eri_ai_chat_open', showAIChat ? '1' : '0'); } catch (e) { /* noop */ }
  }, [showAIChat]);
  const [aiPrompt, setAiPrompt] = useState(null);

  // Preview-mode Guided Path progress lives in-session (see utils/guidedPath.js)
  // so "Mark done" / finished videos move the previewed path forward.
  const [previewPathProgress, setPreviewPathProgress] = useState(() => readPreviewPathProgress());
  useEffect(() => {
    const onProgress = (e) => setPreviewPathProgress(e.detail || {});
    window.addEventListener(PREVIEW_PROGRESS_EVENT, onProgress);
    return () => window.removeEventListener(PREVIEW_PROGRESS_EVENT, onProgress);
  }, []);

  // --- HELPER: CALCULATE DISPLAY USER ---
  const currentActiveUser = React.useMemo(() => {
    if (isPreviewMode) {
        return {
            uid: 'preview-user',
            email: `preview_${previewPersona}@eashub.com`,
            name: `Preview ${previewPersona.charAt(0).toUpperCase() + previewPersona.slice(1)}`,
            organizationId: orgData?.id,
            role: previewRole,
            // Funnel axes so resolveSurface() can reach every group view.
            memberType: previewMemberType,
            ageTier: previewAgeTier,
            groupId: orgData?.groups?.[0] || 'Demo Squad',
            // Sample assessment data so preview renders a populated dashboard
            // (hasData=true, Thriving quadrant) instead of empty-state invites.
            latestStats: PREVIEW_SAMPLE_STATS,
            pulseStreak: 3,
            // 2 days ago -> weekly pulse reads as fresh (isPulseDue < 7 days).
            lastPulseDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            lastAssessmentDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            pathProgress: previewPathProgress
        };
    }

    if (!user) return { name: "Guest" };
    return {
        ...hydratedProfile,
        uid: user.uid,
        email: hydratedProfile?.email || user.email,
        name: hydratedProfile?.name || user.displayName || user.email?.split('@')[0] || "User"
    };
  }, [user, hydratedProfile, isPreviewMode, orgData, previewRole, previewPersona, previewMemberType, previewAgeTier, previewPathProgress]);

  const scores = React.useMemo(() => calculateScores(currentActiveUser), [currentActiveUser]);

  // Journal -> AI Coach context, ONLY when the member opted in (My Journal)
  const journalContext = useJournalContext(currentActiveUser, isPreviewMode);

  // App-wide: a due pulse (or missing baseline) locks the coach on every page.
  // (coachLockedOut is defined below activeSurface: the pulse lock only applies
  // where the pulse exists.)

  // --- SECURITY: ROLE BASED ACCESS ---
  const userIsSuperAdmin = isSuperAdmin(user); 
  // While previewing, impersonate the chosen persona — the real super-admin
  // override must yield so non-exec surfaces can render. Outside preview it still
  // force-routes the actual super-admin to the executive console.
  const isAdmin = currentActiveUser?.role === 'admin' || currentActiveUser?.role === 'super_admin' || (userIsSuperAdmin && !isPreviewMode);

  // The surface a member's type routes to is org-configured (tag != route).
  // Undefined when the org hasn't configured one -> resolveSurface legacy fallback.
  const memberSurface = React.useMemo(() => {
      const types = resolveMemberTypes(orgData);
      return types.find(t => t.id === currentActiveUser?.memberType)?.surface;
  }, [orgData, currentActiveUser?.memberType]);

  // Resolve the member-funnel surface once (consumed by the router below and the
  // Sidebar, so the nav can match the surface a user actually lands on).
  const activeSurface = isAdmin ? 'executive' : resolveSurface({
      role: currentActiveUser?.role,
      memberType: currentActiveUser?.memberType,
      ageTier: resolveAgeTier(currentActiveUser),
      memberSurface,
      youthSurface: orgData?.youthSurface
  });
  const activeTier = resolveAgeTier(currentActiveUser);
  const isMinor = activeTier === 'kid' || activeTier === 'teen';
  // The AI Coach for under-18s is an org setting, on unless switched off.
  const coachAllowed = !isMinor || orgData?.aiCoachForMinors !== false;
  // The weekly-pulse lock belongs to the athlete surface. A parent (no baseline
  // by design) or a family-hub member must not be told to complete a pulse.
  const coachLockedOut = activeSurface === 'athlete' && isDashboardLocked({ user: currentActiveUser, hasData: !!scores?.hasData, isPreviewMode });
  // Whole-person context for the coach (both dimensions, safety, pulse, path step,
  // voice). Built after the surface is known: a parent gets their check-in read
  // about their player, not the athlete summary.
  const coachContext = React.useMemo(
    () => buildCoachContext({ scores, user: currentActiveUser, orgData, journalContext, surface: activeSurface }),
    [scores, currentActiveUser, orgData, journalContext, activeSurface]
  );

  // --- AUTH LISTENER ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      // Tear down any prior profile listeners so they can't survive logout or
      // stack duplicates on the next login.
      if (profileUnsubRef.current) { profileUnsubRef.current(); profileUnsubRef.current = null; }
      // Nothing from the previous account carries into the next one. The join
      // screen signs one person out and the next in within the same tab
      // ("switch account", "add another player"), so the verify banner, the
      // once-per-session role sync and the cadence lock are reset here, not
      // only in handleLogout.
      setVerifyPrompt(null);
      roleSyncRef.current = false;
      setAssessmentDue(false);
      if (metricsUnsubRef.current) { metricsUnsubRef.current(); metricsUnsubRef.current = null; }
      if (currentUser) {
          // Self-only private metrics (latest scores + wearables).
          metricsUnsubRef.current = onSnapshot(doc(db, "users", currentUser.uid, "private_metrics", "latest"),
            (snap) => setPrivateMetrics(snap.exists() ? snap.data() : null),
            (error) => console.error("Private Metrics Sync Error:", error));

          profileUnsubRef.current = onSnapshot(doc(db, "users", currentUser.uid),
            async (docSnap) => {
              try {
              if (docSnap.exists()) {
                  let data = docSnap.data();
                  
                  // --- 1. ORG SYNC & COACH AUTO-ASSIGNMENT ---
                  let limitDays = 30;
                  // A member the org's rosters name as staff whose role is not on the
                  // doc yet (email not verified, so syncMemberRole held it). They are a
                  // coach waiting on a click, not a player who owes a check-in.
                  let pendingStaff = false;
                  if (data.organizationId) {
                      try {
                          const orgSnap = await getDoc(doc(db, "organizations", data.organizationId));
                          if (orgSnap.exists()) {
                              const orgDataSnap = orgSnap.data();
                              limitDays = orgDataSnap.cadenceDays || 30;

                              // Staff roles are assigned by the server (syncMemberRole reads the
                              // org's adminEmails / coaches rosters against the auth token's email).
                              // The client only notices that a sync is due; it can no longer write
                              // role or groupId itself (firestore.rules, 2026-10-10).
                              const userEmail = (currentUser.email || data.email || "").toLowerCase().trim();
                              const isAdminEmail = (orgDataSnap.adminEmails || []).map(e => String(e || '').toLowerCase().trim()).includes(userEmail);
                              // Every roster entry for this email: a coach may be on several teams.
                              // The first is the primary groupId; all of them are groupIds.
                              const rosterTeams = orgDataSnap.features?.enableSquadHub
                                  ? [...new Set((orgDataSnap.coaches || []).filter(c => (c.email || "").toLowerCase().trim() === userEmail && c.groupId).map(c => c.groupId))]
                                  : [];
                              const coachMatch = rosterTeams.length ? { groupId: rosterTeams[0] } : null;
                              const docTeams = Array.isArray(data.groupIds) && data.groupIds.length ? data.groupIds : (data.groupId ? [data.groupId] : []);
                              const sameTeams = docTeams.length === rosterTeams.length && rosterTeams.every(g => docTeams.includes(g));
                              const isStaffRole = data.role === 'admin' || data.role === 'super_admin';
                              const syncDue = (isAdminEmail && !isStaffRole) ||
                                  (!!coachMatch && !isStaffRole && (data.role !== 'coach' || data.groupId !== coachMatch.groupId || !sameTeams));
                              pendingStaff = (isAdminEmail || !!coachMatch) && !isStaffRole && data.role !== 'coach';
                              if (syncDue && !roleSyncRef.current) {
                                  roleSyncRef.current = true; // once per session; the profile listener picks up the write
                                  try {
                                      const res = await httpsCallable(functions, 'syncMemberRole')({});
                                      if (res.data?.changed) { data = { ...data, role: res.data.role, ...(res.data.groupId ? { groupId: res.data.groupId } : {}) }; }
                                      else if (res.data?.needsVerification) { setVerifyPrompt({ email: userEmail, roleLabel: res.data.pendingRole === 'admin' ? 'admin' : 'coach' }); }
                                  } catch (e) { console.error("Role sync failed:", e); }
                              }
                          }
                      } catch(e) { console.error("Org Sync Error:", e); }
                  }

                  setUserProfile(data);

                  // Auto-route to a role's landing page only on the FIRST profile
                  // resolution; later onSnapshot updates must not hijack navigation.
                  const isInitialRoute = !initialRouteDoneRef.current;
                  initialRouteDoneRef.current = true;

                  // --- 2. CADENCE CHECK (Skip for Admins AND Coaches, and for staff still
                  // waiting on email verification: the banner carries them there) ---
                  if (data.role === 'admin' || data.role === 'super_admin' || data.role === 'coach' || isSuperAdmin(currentUser) || pendingStaff) {
                      setAssessmentDue(false);
                      
                      // --- ROUTE ADMINS/COACHES (first load only) ---
                      // A hash the tab inherited from a member session (#assessment,
                      // #pulse) is not where staff land: those are the member's screens.
                      if (isInitialRoute) {
                          const currentHash = window.location.hash.replace('#', '');
                          if (!currentHash || ['dashboard', 'assessment', 'pulse'].includes(currentHash)) {
                              if (data.role === 'admin' || data.role === 'super_admin' || isSuperAdmin(currentUser)) {
                                  handleNavigate('command-center');
                              } else if (data.role === 'coach') {
                                  handleNavigate('squad-dashboard');
                              }
                          }
                      }

                      setLoading(false);
                      return;
                  }

                  // --- ONBOARDING GUARD ---
                  // A user still mid-join (recoverable stub doc, onboardingComplete
                  // === false) has no lastAssessmentDate yet. Without this guard the
                  // cadence check below flips assessmentDue and force-navigates to
                  // #assessment, unmounting the join flow BEFORE the persona/profile
                  // (age/location) steps. Let onboarding finish first; finalizeJoin
                  // sets onboardingComplete=true and the gate then applies normally.
                  if (data.onboardingComplete === false) {
                      setAssessmentDue(false);
                      // Sign-up done, profile not: finish the join before anything else
                      // renders, rather than a half-formed home page.
                      if (data.organizationId && !window.location.hash.startsWith('#join/')) {
                          const resumeTeam = data.groupId || data.joinedFromTeam;
                          const resume = `join/${data.organizationId}${resumeTeam ? `/${encodeURIComponent(resumeTeam)}` : ''}`;
                          setActiveTab(resume);
                          window.location.hash = resume;
                      }
                      setLoading(false);
                      return;
                  }

                  // --- 3. REGULAR USER CADENCE ---
                  let isDue = false;
                  if (!data.lastAssessmentDate) {
                      isDue = true;
                  } else {
                      const lastDate = data.lastAssessmentDate.toDate ?
                        data.lastAssessmentDate.toDate() : new Date(data.lastAssessmentDate);
                      const diffDays = Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24));
                      if (diffDays >= limitDays) isDue = true;
                  }

                  if (isDue) {
                      setAssessmentDue(true);
                      if (window.location.hash !== '#assessment') {
                          window.location.hash = 'assessment';
                          setActiveTab('assessment');
                      }
                  } else {
                      setAssessmentDue(false);

                      // --- ROLE-BASED LANDING PAGE LOGIC (first load only) ---
                      if (isInitialRoute) {
                          const currentHash = window.location.hash.replace('#', '');
                          if (!currentHash || currentHash === 'dashboard') {
                              if (data.role === 'admin' || data.role === 'super_admin' || isSuperAdmin(currentUser)) {
                                  handleNavigate('command-center');
                              } else if (data.role === 'coach') {
                                  handleNavigate('squad-dashboard');
                              } else {
                                  handleNavigate('dashboard');
                              }
                          }
                      }
                  }
              } else {
                  setUserProfile(null);
              }
              } catch (e) {
                  // Whatever failed above, the screen must not stay dark.
                  console.error("Profile listener failed:", e);
              }
              setLoading(false);
          }, 
          (error) => {
              console.error("Profile Sync Error:", error);
              setLoading(false);
          });
      } else {
          setUserProfile(null);
          setPrivateMetrics(null);
          initialRouteDoneRef.current = false; // reset so the next login force-routes again
          setLoading(false);
      }
    });
    return () => {
        unsubscribe();
        if (profileUnsubRef.current) profileUnsubRef.current();
        if (metricsUnsubRef.current) metricsUnsubRef.current();
    };
    // The auth listener is set up once for the life of the app. handleNavigate is
    // recreated on every render; re-subscribing on it would double the listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) setActiveTab(hash);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleNavigate = (tabId) => {
      if (!isPreviewMode && assessmentDue && tabId !== 'assessment') {
          alert("Please complete your scheduled assessment to access the hub.");
          return;
      }
      setActiveTab(tabId);
      window.location.hash = tabId;
  };

  // A tab the org has switched off (utils/navAccess.js) is already rendered as
  // the home screen by renderContent; this brings the URL back in line so the
  // address bar, the sidebar highlight and the screen agree. Skips the cadence
  // lock (handleNavigate would alert) and admins, who are exempt from the list.
  useEffect(() => {
      if (loading || isAdmin || !orgData) return;
      if (assessmentDue && !isPreviewMode) return;
      if (activeTab.startsWith('join/')) return; // the join screen is not an org tab
      if (!isTabAllowed(orgData, activeTab, activeTier)) {
          setActiveTab('dashboard');
          window.location.hash = 'dashboard';
      }
  }, [activeTab, orgData, isAdmin, loading, assessmentDue, isPreviewMode, activeTier]);

  const handleLogout = async () => {
    try { 
        if (isPreviewMode) exitPreviewMode();
        await signOut(auth);
        setUserProfile(null); 
        setAssessmentDue(false);
        handleNavigate('dashboard'); 
    } catch (error) { console.error(error); }
  };

  const handleAiTrigger = (text) => {
      setAiPrompt(text);
      setShowAIChat(true); // Always force the chat panel open
  };
  // --- PREVIEW HANDLERS ---
  const handlePreviewOrg = (orgId) => {
      enterPreviewMode(orgId);
      setIsPreviewCollapsed(false);
      handleNavigate('dashboard');
  };

  const handleExitPreview = () => {
      exitPreviewMode();
      // The super admin goes back to the panel they came from; an organization's
      // own admin goes back to their console.
      handleNavigate(userIsSuperAdmin ? 'super-admin' : 'command-center');
  };

  const handlePersonaSwitch = (personaId) => {
      switchPreviewPersona(personaId);
      // Land on each persona's home tab. The member-funnel surfaces (spouse/
      // teen/kidz) are intercepted by resolveSurface regardless of tab, so
      // 'dashboard' is a safe default for all non-staff personas.
      if (personaId === 'exec') handleNavigate('command-center');
      else if (personaId === 'coach') handleNavigate('squad-dashboard');
      else handleNavigate('dashboard');
  };

  // =============================================================================
  // THE ROLE-BASED ROUTER
  // =============================================================================
  const renderContent = () => {
      // ORG CONTENT ALLOWLIST. A tab the org has switched off renders as the
      // home screen instead, and the effect below corrects the URL. Done here,
      // ahead of every portal, so no surface can forget it. Admins are exempt:
      // they configure this list and need to see what they are hiding.
      const tabOk = isAdmin || isTabAllowed(orgData, activeTab, activeTier);
      const orgMH = allowedSubTopicIds(orgData, 'mental-health', activeTier);
      const orgMP = allowedSubTopicIds(orgData, 'mental-performance', activeTier);

      // A section tab (#mental-health) lands on its first ALLOWED sub-topic. The
      // portals hardcode 'anxiety' / 'resilience', which an allowlist can remove.
      let effectiveTab = tabOk ? activeTab : 'dashboard';
      if (tabOk && !isAdmin && NAV_TOPIC_GROUPS[activeTab]) {
          effectiveTab = firstAllowedTopic(orgData, activeTab, activeTier) || 'dashboard';
      }
      // Same for an org-defined section (utils/customNav.js): a section with
      // topics is a heading, not a page, so it lands on its first topic.
      const customSection = findCustomSection(orgData, activeTab);
      if (tabOk && customSection && customSection.subItems.length > 0) effectiveTab = customSection.subItems[0].id;

      // An org-defined LANDING PAGE is itself the page, on every surface that
      // has topic routes. Rendered here, ahead of the portals, so admins, coaches,
      // players, family and teen members all get the same page. The kids surface
      // is a single self-contained view and does not offer custom pages.
      if (tabOk && isLandingSection(customSection) && activeSurface !== 'kidz') {
          return <CustomLandingView section={customSection} onNavigate={handleNavigate} />;
      }

      // Org-defined topics are ordinary topic pages, so the family and teen
      // surfaces accept them alongside the Mental Performance list.
      const orgTopics = [...orgMP, ...customTopicIds(orgData)];
      const memberTab = customSection ? effectiveTab : activeTab;

      const portalProps = {
          activeTab: effectiveTab,
          handleNavigate,
          currentUser: currentActiveUser,
          handleAiTrigger,
          isSuperAdmin: userIsSuperAdmin,
          handlePreviewOrg
      };

      // Email-based super admins are forced to the executive console even when
      // their user-doc role hasn't caught up; keep that override ahead of the resolver.
      if (isAdmin) return <ExecutivePortal {...portalProps} />;

      // Surface resolved once at the component level (see activeSurface). memberType
      // + ageTier come off the user doc and are stamped at join by JoinOrgHandler;
      // absent them, non-staff resolve to the adult athlete realm.
      switch (activeSurface) {
          case 'executive': return <ExecutivePortal {...portalProps} />;
          case 'coach':   return <CoachPortal {...portalProps} />;
          case 'family': {
              // Partner-authored money content; the hub filters by the member's tier.
              if (activeTab === FINANCE_TAB_ID && isFinanceHubEnabled(orgData)) {
                  return <FinanceHubView currentUser={currentActiveUser} />;
              }
              const topicId = resolveMemberTopic(memberTab, orgMH, orgTopics);
              return topicId
                  ? <TopicView topicId={topicId} contentData={initialTopicContent} onAskAi={handleAiTrigger} currentUser={currentActiveUser} />
                  : <SpouseHubView currentUser={currentActiveUser} />;
          }
          case 'parent': {
              // Youth-sport parent: the hub is home. Everything else the org offers
              // (topics, Sleep, Brain Health, Rest & Recharge, the journal, the org's
              // own sections) routes exactly as it does for an adult member.
              if (['dashboard', 'assessment', 'pulse'].includes(effectiveTab)) {
                  return <ParentHubView currentUser={currentActiveUser} onNavigate={handleNavigate} />;
              }
              return <AthletePortal {...portalProps} />;
          }
          case 'kidz':    return <KidzCorner contentData={initialTopicContent} currentUser={currentActiveUser} />;
          case 'teen': {
              if (activeTab === FINANCE_TAB_ID && isFinanceHubEnabled(orgData)) {
                  return <FinanceHubView currentUser={currentActiveUser} />;
              }
              const topicId = resolveMemberTopic(memberTab, orgMH.filter(id => !TEEN_EXCLUDED_TOPIC_IDS.includes(id)), orgTopics);
              return topicId
                  ? <TopicView topicId={topicId} contentData={initialTopicContent} onAskAi={handleAiTrigger} currentUser={currentActiveUser} />
                  : <TeenHubView currentUser={currentActiveUser} />;
          }
          case 'athlete':
          default:        return <AthletePortal {...portalProps} />;
      }
  };
  
  const activeAgentConfig = MAIN_AGENT_CONFIG;
  // This hides the AI Coach panel for ALL Admin, Super Admin, and Command Center views
  const isFullWidthView = activeTab === 'admin' || activeTab === 'super-admin' || activeTab === 'command-center' || activeTab.startsWith('cc-') || activeTab.startsWith('join/');
  // Workforce Analytics is a page of its own: the hub sidebar gives way to the
  // console's (enterprise/ConsoleSidebar.jsx), which lists only the console tabs.
  const isConsoleView = isAdmin && isConsoleTab(activeTab);

  // Hold the screen until the organization's theme is in, so a Hockey Alberta
  // member never sees a flash of the generic hub first.
  const loadingScreen = <LoadingScreen />;
  // The theme and stub holds below are a courtesy, never a lock: if the organization
  // read is slow or fails, the screen gives way after a few seconds and the app
  // renders as it always did (a brief default look at worst).
  if (loading || (user && !isPreviewMode && !themeLoaded && !holdExpired)) return loadingScreen;
  // A half-joined account (sign-up done, profile not) is on its way back to the
  // join screen (the profile listener sets the hash); nothing else renders first.
  if (user && !isPreviewMode && userProfile?.onboardingComplete === false && userProfile?.organizationId && !activeTab.startsWith('join/') && !holdExpired) return loadingScreen;
  
  if (activeTab.startsWith('join/')) {
      // #join/{orgId}                      -> the org link (the member picks a team)
      // #join/{orgId}/{team}               -> a team's link (the team is pre-selected and locked)
      // #join/{orgId}/{team}/staff/{code}  -> the team's staff link (bench staff join onto the coaches roster)
      const [, orgId, teamPart, kind, codePart] = activeTab.split('/');
      let joinTeam = null;
      try { joinTeam = teamPart ? decodeURIComponent(teamPart) : null; } catch (e) { joinTeam = null; }
      let staffCode = null;
      try { staffCode = kind === 'staff' && codePart ? decodeURIComponent(codePart) : null; } catch (e) { staffCode = null; }
      return (
        <div className="h-screen bg-slate-50 overflow-y-auto">
            <JoinOrgHandler orgId={orgId} team={joinTeam} staffCode={staffCode} onComplete={(joinedAs) => {
                // A member starts their baseline; a coach goes to the team, an admin to the
                // console; staff still waiting on email verification land on the home page
                // with the verify banner (the cadence gate leaves them alone).
                if (joinedAs === 'coach') handleNavigate('squad-dashboard');
                else if (joinedAs === 'admin') handleNavigate('command-center');
                else if (joinedAs === 'pending' || joinedAs === 'home') handleNavigate('dashboard');
                else handleNavigate('assessment');
            }} />
        </div>
      );
  }

  if (!user && !isPreviewMode) {
      return (
        <>
            <UnauthLanding onLoginClick={() => setShowLoginModal(true)} />
            <LoginModal 
                isOpen={showLoginModal} 
                onClose={() => setShowLoginModal(false)} 
                onSuccess={() => {}}
            />
        </>
      );
  }

  return (
    <div className="flex h-screen overflow-hidden relative" style={{ backgroundColor: 'var(--brand-light)' }}>
      
      {isConsoleView ? (
        <ConsoleSidebar
          activeTab={activeTab}
          navigateTo={handleNavigate}
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          currentUser={currentActiveUser}
          currentOrg={orgData}
          isSuperAdmin={userIsSuperAdmin}
          onLogout={handleLogout}
        />
      ) : (
        <Sidebar
          activeTab={activeTab === 'assessment' ? 'dashboard' : activeTab}
          navigateTo={handleNavigate}
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          isAdmin={isAdmin}
          isSuperAdmin={userIsSuperAdmin}
          surface={activeSurface}
          currentUser={currentActiveUser}
          currentOrg={orgData}
          onOpenAdmin={() => handleNavigate('admin')}
          // An organization's own admin can see their hub as each kind of member
          // sees it: the super admin's preview, scoped to the admin's own org.
          onViewAs={!isPreviewMode && userProfile?.role === 'admin' && userProfile?.organizationId ? () => handlePreviewOrg(userProfile.organizationId) : null}
          onLogout={handleLogout} 
        />
      )}

      <div className="flex-1 flex flex-col min-w-0 relative">
        <div className="lg:hidden h-16 bg-white border-b flex items-center px-4 shrink-0 gap-4">
          <button onClick={() => setIsSidebarOpen(true)} className="p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg"><Menu size={24} /></button>
          <div className="flex flex-col leading-none select-none">
            <div className="tracking-widest text-lg">
                <span className="font-black text-slate-900">
                    {orgData?.name ? orgData.name.split(' ')[0].toUpperCase() : 'ERI'}
                </span>
                <span className="font-light text-brand-primary ml-1">
                  {orgData?.name ? (orgData.name.split(' ')[1] || 'HUB') : 'HUB'}
                </span>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-hidden relative">
            <div className="max-w-[1920px] mx-auto flex h-full">
                <div className={`flex-1 overflow-y-auto pb-24 lg:pb-0 h-full ${isConsoleView ? 'p-0' : 'p-6'} no-scrollbar ${isFullWidthView ? 'w-full' : ''}`}>
                    {verifyPrompt && !isPreviewMode && (
                        <VerifyEmailBanner email={verifyPrompt.email} roleLabel={verifyPrompt.roleLabel} onVerified={(res) => {
                            setVerifyPrompt(null); roleSyncRef.current = false;
                            // The role is on the doc now; take them where it lives.
                            if (res?.role === 'coach') handleNavigate('squad-dashboard');
                            else if (res?.role === 'admin' || res?.role === 'super_admin') handleNavigate('command-center');
                        }} />
                    )}
                    {renderContent()}
                </div>

                {/* AI COACH DRAWER — overlay on small screens, side panel (page stays
                    interactive) on lg+. Toggled by the floating button below. */}
                {!isFullWidthView && coachAllowed && showAIChat && (
                    <div className="fixed inset-0 z-[90] bg-slate-900/40 lg:hidden animate-in fade-in duration-200" onClick={() => setShowAIChat(false)} aria-hidden="true" />
                )}
                {!isFullWidthView && coachAllowed && (
                    <div className={`
                        w-full sm:w-[400px] border-l border-slate-200 bg-white h-full shrink-0 flex flex-col
                        fixed right-0 top-0 bottom-0 z-[100] shadow-2xl transform transition-transform duration-300
                        ${showAIChat ? 'translate-x-0' : 'translate-x-full'}
                    `}>
                        <div className="p-3 border-b flex items-center justify-between bg-slate-50 shrink-0">
                          <span className="text-xs font-black uppercase tracking-widest text-slate-500 pl-1">AI Coach</span>
                          <button onClick={() => setShowAIChat(false)} className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition-colors" aria-label="Close AI Coach"><X size={18} /></button>
                        </div>
                        <div className="flex-1 relative w-full h-full overflow-hidden">
                            {coachLockedOut && (
                                <div className="absolute inset-0 z-20 bg-white/60 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-200">
                                    <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 text-center max-w-xs">
                                        <div className="w-12 h-12 rounded-2xl bg-brand-light text-brand-primary flex items-center justify-center mx-auto mb-3"><Lock size={22} /></div>
                                        <p className="font-black text-slate-900">{scores?.hasData ? 'Complete your 10-second pulse' : 'Complete your baseline assessment'}</p>
                                        <p className="text-sm text-slate-500 font-medium mt-1">to view and interact with your coach.</p>
                                        <button
                                            onClick={() => { setShowAIChat(false); handleNavigate(scores?.hasData ? 'pulse' : 'assessment'); }}
                                            className="mt-4 w-full py-3 bg-brand-primary hover:bg-brand-dark text-white rounded-xl font-bold transition-colors"
                                        >
                                            {scores?.hasData ? 'Take the pulse' : 'Start baseline'}
                                        </button>
                                    </div>
                                </div>
                            )}
                            <AICoach 
                                isActive={true} 
                                agentConfig={activeAgentConfig} 
                                navigateTo={handleNavigate} 
                                externalPrompt={aiPrompt}
                                onPromptHandled={() => setAiPrompt(null)} // ONLY clear the prompt, don't close the chat!
                                onCloseChat={() => setShowAIChat(false)}  // Pass a dedicated close function
                                insights={scores.insights}
                                journalContext={journalContext}
                                scores={scores}
                                currentUser={currentActiveUser}
                                coachContext={coachContext}
                            />
                        </div>
                    </div>
                )}
            </div>
        </main>

        {/* --- ASSESSMENT / PULSE OVERLAY --- */}
        {(activeTab === 'assessment' || activeTab === 'pulse') && (
            <div className="fixed inset-0 z-[300] bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="w-full h-full max-w-5xl bg-slate-50 rounded-3xl overflow-hidden relative shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col">
                    
                    {!assessmentDue && (
                        <button 
                            onClick={() => handleNavigate('dashboard')} 
                            className="absolute top-2.5 right-4 p-2 bg-white rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors z-50 shadow-sm"
                        >
                            <X size={24} />
                        </button>
                    )}
                    
                    {assessmentDue && !isPreviewMode && (
                        <div className="bg-red-500 text-white text-center py-2 px-4 text-xs font-bold uppercase tracking-widest flex items-center justify-between gap-2 shadow-md z-50">
                            <div className="flex items-center gap-2 flex-1 justify-center">
                                <Lock size={14}/> Assessment Required to Unlock Dashboard
                            </div>
                            <button 
                                onClick={handleLogout}
                                className="flex items-center gap-1 bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-[10px] transition-colors border border-red-400"
                            >
                                <LogOut size={12}/> Not you? Sign Out
                            </button>
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        <AssessmentView 
                            navigateTo={handleNavigate} 
                            currentUser={currentActiveUser} 
                            onAskAi={handleAiTrigger}
                            onCoachReveal={() => setShowAIChat(true)} // drawer slides in after an assessment so people know it's there
                            isPulseMode={activeTab === 'pulse'} 
                        />
                    </div>
                </div>
            </div>
        )}

        {/* --- DYNAMIC PREVIEW OVERLAY --- */}
        {isPreviewMode && isPreviewCollapsed && (
            <button
                onClick={() => setIsPreviewCollapsed(false)}
                className="fixed bottom-6 left-6 z-[400] w-11 h-11 rounded-full bg-slate-900/90 hover:bg-slate-900 border border-slate-700 text-white flex items-center justify-center shadow-2xl animate-in fade-in"
                title="Show preview controls"
            >
                <Eye size={18} />
            </button>
        )}

        {isPreviewMode && !isPreviewCollapsed && (
            <div className="fixed bottom-6 left-6 z-[400] flex flex-col gap-3 bg-slate-900 p-4 rounded-2xl shadow-2xl border border-slate-700 animate-in slide-in-from-bottom-4 w-96 max-w-[calc(100vw-3rem)]">
                <div className="flex items-center gap-3 text-white">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center animate-pulse shrink-0">
                        <Eye size={20} />
                    </div>
                    <div className="flex-1 min-w-0 pr-1">
                        <p className="text-xs font-bold uppercase text-slate-400 tracking-wider">Viewing As</p>
                        <p className="font-bold text-sm truncate">{orgData?.name || "Generic Org"}</p>
                    </div>
                    <button
                        onClick={() => setIsPreviewCollapsed(true)}
                        className="p-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors shrink-0"
                        title="Collapse for screenshot"
                    >
                        <ChevronDown size={18} />
                    </button>
                    <button
                        onClick={handleExitPreview}
                        className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors shrink-0"
                        title="Exit Preview"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* PERSONA SWITCHER — every member-funnel group view */}
                {(() => {
                    const corporate = isCorporateOrg(orgData);
                    // In the org's own words (Player / Parent for Hockey Alberta); the
                    // platform names only when the org has not configured its types.
                    const memberPersonas = previewPersonasFor(orgData, { athlete: copyFor('roleUser', corporate) });
                    const staffPersonas = [
                        { id: 'coach', label: copyFor('roleManager', corporate) },
                        { id: 'exec',  label: copyFor('personaExec', corporate) },
                    ];
                    const btn = (p) => (
                        <button
                            key={p.id}
                            onClick={() => handlePersonaSwitch(p.id)}
                            className={`py-2 px-1 min-w-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wide rounded-lg transition-colors ${previewPersona === p.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            {p.label}
                        </button>
                    );
                    return (
                        <div className="space-y-2">
                            <div>
                                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1 px-1">Member surfaces</p>
                                <div className={`grid bg-slate-800 p-1 rounded-xl gap-1 ${memberPersonas.length <= 2 ? 'grid-cols-2' : memberPersonas.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                                    {memberPersonas.map(btn)}
                                </div>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1 px-1">Staff surfaces</p>
                                <div className="grid grid-cols-2 bg-slate-800 p-1 rounded-xl gap-1">
                                    {staffPersonas.map(btn)}
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        )}

        {!isFullWidthView && coachAllowed && activeTab !== 'assessment' && activeTab !== 'pulse' && (
            <button 
                onClick={() => setShowAIChat(!showAIChat)} 
                className={`fixed bottom-6 right-6 z-[110] flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 text-white rounded-full shadow-lg shadow-indigo-500/30 border border-white/20 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 ${showAIChat ? 'hidden' : 'flex'}`}
            >
                <MessageSquare size={20} className="fill-white/10" />
                <span className="font-bold text-sm">AI Coach</span>
            </button>
        )}

        <LoginModal 
            isOpen={showLoginModal} 
            onClose={() => setShowLoginModal(false)} 
            onSuccess={() => handleNavigate('admin')}
        />

      </div>
    </div>
  );
};

function App() {
    return (
        <OrganizationProvider>
            <AppContent />
        </OrganizationProvider>
    );
}

export default App;